import type { HexColor } from '@miyuki/types';
import {
  Button,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@miyuki/ui';
import { Trash2Icon } from 'lucide-react';

import { t } from '@/lib/i18n';

/**
 * Editor de un color de la paleta.
 *
 * El selector es el nativo del navegador (`input[type=color]`): cubre el
 * espacio RGB completo, en móvil abre la rueda del sistema y no pesa un solo
 * kilobyte en el bundle. El hexadecimal se muestra al lado (RF-13).
 */
export interface ColorEditorSheetProps {
  readonly open: boolean;
  readonly color: HexColor | null;
  readonly index: number | null;
  readonly beadsUsingColor: number;
  readonly canRemove: boolean;
  readonly onChange: (color: HexColor) => void;
  readonly onRemove: () => void;
  readonly onClose: () => void;
}

export function ColorEditorSheet({
  open,
  color,
  index,
  beadsUsingColor,
  canRemove,
  onChange,
  onRemove,
  onClose,
}: ColorEditorSheetProps) {
  if (index === null || color === null) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="bottom" className="pb-8">
        <SheetHeader>
          <SheetTitle>{t('editColor')}</SheetTitle>
          <SheetDescription>{t('reassignBeadsBody', { n: beadsUsingColor })}</SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-4 px-4">
          <Label htmlFor="color-picker" className="sr-only">
            {t('editColor')}
          </Label>
          {/* El valor lo manda la paleta: `onChange` despacha y el color vuelve
              como prop, así que no hace falta estado local que sincronizar. */}
          <input
            id="color-picker"
            type="color"
            value={color}
            className="border-border size-16 cursor-pointer rounded-lg border bg-transparent"
            onChange={(event) => {
              onChange(event.target.value.toLowerCase() as HexColor);
            }}
          />
          <output className="font-mono text-lg uppercase">{color}</output>
        </div>

        <div className="px-4 pb-2">
          <Button
            variant="outline"
            size="touch"
            className="text-destructive w-full"
            disabled={!canRemove}
            onClick={onRemove}
          >
            <Trash2Icon />
            {t('removeColor')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
